/**
 * Client state for the Alerts workspace.
 *
 * Queue and History keep INDEPENDENT filter, pagination and selection state —
 * switching modes must never carry one's query model into the other, and a
 * History range change must not disturb where the operator is in the Queue.
 *
 * Every setter that changes the request identity also resets that side's
 * pagination, because a cursor or offset only means anything relative to the
 * query that produced it. Those resets are the reason this lives in a store
 * instead of scattered `useState`s: they are the part worth testing.
 *
 * Only the SCOPE is persisted. An operator returns to the location and machines
 * they work, but restoring a cursor, an offset or a selection would put them
 * back on a page of a mutable queue that no longer exists — so those are
 * deliberately left out of `partialize`.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AlertOrdering, AlertStatus } from "@/alerts/types/machine-alert.ts";

/** Page sizes offered in both modes. */
export const ALERT_PAGE_SIZES = [25, 50, 100] as const;

export const DEFAULT_ALERT_PAGE_SIZE = 50;

interface AlertsWorkspaceState {
  /** Set on org change so a stale scope from another tenant can't be queried. */
  organizationId: number | null;

  /* --- Queue ------------------------------------------------------- */
  queueLocationId: number | null;
  queueMachineIds: number[];
  queueTypes: string[];
  queueLimit: number;
  /** Opaque server cursor; null = first page. */
  queueCursor: string | null;
  selectedQueueAlertId: number | null;

  /* --- History ----------------------------------------------------- */
  historyLocationId: number | null;
  historyMachineIds: number[];
  historyStatuses: AlertStatus[];
  historyTypes: string[];
  historyLabels: string[];
  /** null = unset, so the bound is omitted rather than sent as 0 or 1. */
  historyMinConfidence: number | null;
  historyMaxConfidence: number | null;
  historyOrdering: AlertOrdering;
  historyLimit: number;
  historyOffset: number;
  selectedHistoryAlertId: number | null;

  syncOrganization: (organizationId: number | null) => void;

  setQueueLocation: (locationId: number | null) => void;
  setQueueMachines: (machineIds: number[]) => void;
  setQueueTypes: (types: string[]) => void;
  setQueueLimit: (limit: number) => void;
  /** Navigate with a cursor the server handed us. Never construct one. */
  goToQueueCursor: (cursor: string | null) => void;
  /** A page that emptied out under us: drop the cursor chain, reload page one. */
  recoverEmptyQueuePage: () => void;
  selectQueueAlert: (alertId: number | null) => void;

  setHistoryLocation: (locationId: number | null) => void;
  setHistoryMachines: (machineIds: number[]) => void;
  setHistoryStatuses: (statuses: AlertStatus[]) => void;
  setHistoryTypes: (types: string[]) => void;
  setHistoryLabels: (labels: string[]) => void;
  setHistoryConfidence: (min: number | null, max: number | null) => void;
  setHistoryOrdering: (ordering: AlertOrdering) => void;
  setHistoryLimit: (limit: number) => void;
  setHistoryOffset: (offset: number) => void;
  /** Range changes come from the shared picker, not from a control in History. */
  resetHistoryPagination: () => void;
  selectHistoryAlert: (alertId: number | null) => void;
}

/** Paging a mutable list also drops the selection — the row may not survive. */
const QUEUE_RESET = { queueCursor: null, selectedQueueAlertId: null } as const;
const HISTORY_RESET = { historyOffset: 0, selectedHistoryAlertId: null } as const;

export const useAlertsWorkspaceStore = create<AlertsWorkspaceState>()(
  persist(
    (set, get) => ({
      organizationId: null,

      queueLocationId: null,
      queueMachineIds: [],
      queueTypes: [],
      queueLimit: DEFAULT_ALERT_PAGE_SIZE,
      queueCursor: null,
      selectedQueueAlertId: null,

      historyLocationId: null,
      historyMachineIds: [],
      historyStatuses: [],
      historyTypes: [],
      historyLabels: [],
      historyMinConfidence: null,
      historyMaxConfidence: null,
      historyOrdering: "-timestamp",
      historyLimit: DEFAULT_ALERT_PAGE_SIZE,
      historyOffset: 0,
      selectedHistoryAlertId: null,

      syncOrganization: (organizationId) => {
        if (get().organizationId === organizationId) return;
        // A different tenant shares nothing with the previous one — scope,
        // cursor, offset and selection all belong to the org that produced them.
        set({
          organizationId,
          queueLocationId: null,
          queueMachineIds: [],
          historyLocationId: null,
          historyMachineIds: [],
          ...QUEUE_RESET,
          ...HISTORY_RESET,
        });
      },

      setQueueLocation: (queueLocationId) =>
        set({
          queueLocationId,
          // The machine scope belonged to the previous location.
          queueMachineIds: [],
          ...QUEUE_RESET,
        }),
      setQueueMachines: (queueMachineIds) => set({ queueMachineIds, ...QUEUE_RESET }),
      setQueueTypes: (queueTypes) => set({ queueTypes, ...QUEUE_RESET }),
      setQueueLimit: (queueLimit) => set({ queueLimit, ...QUEUE_RESET }),
      goToQueueCursor: (queueCursor) => set({ queueCursor, selectedQueueAlertId: null }),
      recoverEmptyQueuePage: () => set(QUEUE_RESET),
      selectQueueAlert: (selectedQueueAlertId) => set({ selectedQueueAlertId }),

      setHistoryLocation: (historyLocationId) =>
        set({ historyLocationId, historyMachineIds: [], ...HISTORY_RESET }),
      setHistoryMachines: (historyMachineIds) => set({ historyMachineIds, ...HISTORY_RESET }),
      setHistoryStatuses: (historyStatuses) => set({ historyStatuses, ...HISTORY_RESET }),
      setHistoryTypes: (historyTypes) => set({ historyTypes, ...HISTORY_RESET }),
      setHistoryLabels: (historyLabels) => set({ historyLabels, ...HISTORY_RESET }),
      setHistoryConfidence: (historyMinConfidence, historyMaxConfidence) =>
        set({ historyMinConfidence, historyMaxConfidence, ...HISTORY_RESET }),
      setHistoryOrdering: (historyOrdering) => set({ historyOrdering, ...HISTORY_RESET }),
      setHistoryLimit: (historyLimit) => set({ historyLimit, ...HISTORY_RESET }),
      setHistoryOffset: (historyOffset) =>
        set({ historyOffset: Math.max(0, historyOffset), selectedHistoryAlertId: null }),
      resetHistoryPagination: () => set(HISTORY_RESET),
      selectHistoryAlert: (selectedHistoryAlertId) => set({ selectedHistoryAlertId }),
    }),
    {
      name: "maestro-alerts-workspace",
      storage: createJSONStorage(() => localStorage),
      // Scope and page size only. Cursors, offsets and selections describe a
      // moment in a mutating list and mean nothing on the next visit.
      partialize: (state) => ({
        organizationId: state.organizationId,
        queueLocationId: state.queueLocationId,
        queueMachineIds: state.queueMachineIds,
        queueTypes: state.queueTypes,
        queueLimit: state.queueLimit,
        historyLocationId: state.historyLocationId,
        historyMachineIds: state.historyMachineIds,
        historyStatuses: state.historyStatuses,
        historyTypes: state.historyTypes,
        historyLabels: state.historyLabels,
        historyMinConfidence: state.historyMinConfidence,
        historyMaxConfidence: state.historyMaxConfidence,
        historyOrdering: state.historyOrdering,
        historyLimit: state.historyLimit,
      }),
    },
  ),
);
